import { motion } from "framer-motion";
import {
  ShieldCheck,
  Radar,
  Sparkles,
  Users,
  Zap,
  Lock,
  Cpu,
} from "lucide-react";

const FEATURES = [
  {
    icon: Radar,
    title: "Segnali Puri e Analisi Pulite",
    desc: "Flussi di dati e indicatori organizzati algoritmicamente. Solo informazioni essenziali, senza rumore di fondo o distrazioni. Pochi dati necessari, pochi passaggi per la compilazione e una struttura semplice e chiara.",
    span: "md:col-span-8",
    tall: false,
  },
  {
    icon: ShieldCheck,
    title: "Privacy Totale",
    desc: "Nessun tracciamento dei tuoi dati personali. La tua operatività resta totalmente privata e protetta per impostazione predefinita.",
    span: "md:col-span-4",
    tall: false,
  },
  {
    icon: Users,
    title: "Community Premium",
    desc: "Un ecosistema a porte chiuse che cresce e mantiene un ambiente sano. Confrontati con altri utenti orientati a un approccio strutturato all'analisi.",
    span: "md:col-span-4",
    tall: false,
  },
  {
    icon: Sparkles,
    title: "Rilasci e Utility Esclusive",
    desc: "Prendi parte all'evoluzione della piattaforma con aggiornamenti sviluppati anche su input della Community. Strumenti di calcolo e metriche rilasciati direttamente nel tuo Hub.",
    span: "md:col-span-8",
    tall: false,
  },
  {
    icon: Zap,
    title: "Elaborazione Immediata",
    desc: "Un flusso semplice permette di passare dall'inserimento dei dati alla lettura dell'analisi in pochi passaggi, senza disperdersi tra strumenti differenti.",
    span: "md:col-span-4",
  },
  {
    icon: Lock,
    title: "Indicatori Chiari",
    desc: "Probabilità, indicatori e movimenti vengono organizzati in una struttura pensata per rendere più immediata la lettura dell'analisi.",
    span: "md:col-span-4",
  },
  
    {
    icon: Cpu,
    title: "Metodo e Trasparenza",
    desc: "ZeroSignals organizza dati e indicatori quantitativi per supportare un processo di analisi più strutturato. Nessuna promessa di risultato: i dati restano uno strumento a supporto delle decisioni.",
    span: "md:col-span-4",
  },
];

export default function WhatWeDo() {
  return (
    <section
      id="ilprogetto"
      data-testid="what-we-do-section"
      className="relative border-t border-brand-gold/20 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-14 grid grid-cols-1 items-end gap-8 lg:grid-cols-12"
        >
          <div className="lg:col-span-7">
            <div className="mb-5 inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.28em] text-brand-neon-blue">
              <span className="h-px w-4 bg-brand-gold" />
              Il Progetto ZeroSignals
            </div>
            <h2 className="font-display text-4xl font-black leading-[1.02] tracking-tighter md:text-5xl text-white">
              I pilastri del progetto <br />
              <span className="text-gradient-gold">ZeroSignals.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="max-w-md text-base leading-relaxed text-white/60 sm:text-lg">
              Sette caratteristiche che definiscono la piattaforma. Un unico ecosistema, pensato per rendere l&apos;analisi più chiara, veloce e strutturata.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                className={`group relative col-span-1 ${f.span} overflow-hidden rounded-2xl border border-white/5 bg-[#121212] p-7 transition-all hover:border-brand-gold/40 hover:bg-[#161616]`}
                data-testid={`feature-card-${i}`}
              >
                {/* Inner gradient on hover */}
                <div className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-brand-neon-blue/0 blur-3xl transition-all duration-500 group-hover:bg-brand-neon-blue/15" />

                <div className="relative flex items-center justify-between">
                  <div className="grid h-12 w-12 place-items-center rounded-xl border border-brand-neon-blue/30 bg-brand-neon-blue/[0.06] text-brand-neon-blue transition-all duration-300 group-hover:scale-110 group-hover:border-brand-gold/60 group-hover:text-brand-gold group-hover:shadow-[0_0_24px_-6px_rgba(212,175,55,0.7)]">
                    <Icon size={20} strokeWidth={1.6} />
                  </div>
                  <span className="font-display text-[10px] uppercase tracking-[0.24em] text-white/30">
                    0{i + 1}
                  </span>
                </div>

                <h3 className="mt-6 font-display text-xl font-bold leading-tight tracking-tight text-white">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/55">
                  {f.desc}
                </p>

                <div className="mt-6 h-px w-full origin-left scale-x-0 bg-gradient-to-r from-brand-gold via-brand-neon-blue to-transparent transition-transform duration-500 group-hover:scale-x-100" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}